import { z } from 'zod';

export const CALL_DIRECTIONS = ['outgoing', 'incoming', 'missed', 'rejected'] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];

/** One call from the phone's call log, as sent by the mobile app. */
export const deviceCallSchema = z.object({
  phone: z.string().trim().min(3).max(30),
  direction: z.enum(CALL_DIRECTIONS),
  startedAt: z.iso.datetime({ offset: true }),
  durationSec: z.coerce.number().int().min(0).max(24 * 3600),
  simSlot: z.coerce.number().int().min(0).max(4).nullable().optional(),
});

export const callSyncSchema = z.object({
  deviceId: z.uuid().optional(),
  calls: z.array(deviceCallSchema).max(500),
});

/** A call logged by hand from the web (e.g. called from a desk phone). */
export const manualCallSchema = z.object({
  leadId: z.uuid(),
  direction: z.enum(['outgoing', 'incoming']),
  durationMin: z.coerce.number().min(0).max(600).default(0),
  connected: z.boolean().default(true),
  note: z.string().trim().max(2000).optional(),
  at: z.iso.datetime().optional(),
});

export const callListQuerySchema = z.object({
  user: z.uuid().optional(),
  lead: z.uuid().optional(),
  direction: z.enum([...CALL_DIRECTIONS, 'connected', 'not_connected']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  recorded: z.enum(['yes']).optional(),
  analysed: z.enum(['yes']).optional(),
  sentiment: z.enum(['positive', 'neutral', 'negative']).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(10).max(200).default(50),
});

const coord = {
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  accuracy: z.coerce.number().min(0).max(100_000).optional(),
};

export const checkInSchema = z.object({
  ...coord,
  lat: coord.lat.optional(),
  lng: coord.lng.optional(),
  note: z.string().trim().max(500).optional(),
  device: z.string().max(100).optional(),
});

export const pingBatchSchema = z.object({
  pings: z
    .array(z.object({
      ...coord,
      at: z.iso.datetime({ offset: true }),
      speed: z.coerce.number().min(0).max(500).optional(),
      battery: z.coerce.number().int().min(0).max(100).optional(),
    }))
    .min(1)
    .max(1000),
});

export const attendanceSettingsSchema = z.object({
  officeStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: 'Use a time like 09:30.' }),
  graceMinutes: z.coerce.number().int().min(0).max(240),
  requireLocation: z.boolean(),
  office: z
    .object({ lat: coord.lat, lng: coord.lng, radiusM: z.coerce.number().int().min(50).max(50_000) })
    .nullable(),
});
export type AttendanceSettings = z.infer<typeof attendanceSettingsSchema>;
export const DEFAULT_ATTENDANCE_SETTINGS: AttendanceSettings = { officeStart: '09:30', graceMinutes: 15, requireLocation: true, office: null };

export const callSettingsSchema = z.object({
  /** Create a lead automatically when an unknown number calls a salesperson. */
  autoCreateLeads: z.enum(['off', 'missed', 'incoming']),
  trackingEnabled: z.boolean(),
  trackingIntervalSec: z.coerce.number().int().min(60).max(3600),
  ratePerKm: z.coerce.number().min(0).max(100),
});
export type CallSettings = z.infer<typeof callSettingsSchema>;
export const DEFAULT_CALL_SETTINGS: CallSettings = { autoCreateLeads: 'missed', trackingEnabled: false, trackingIntervalSec: 300, ratePerKm: 0 };
